/**
 * <Avatar />
 *
 * Circular initial-bubble used across the forum + profile surfaces.
 * Derives a stable brand-palette background from the username hash so
 * users are visually recognisable without needing real avatars.
 */
import * as React from "react";
import { cn } from "../../lib/cn";

interface Props {
  /** Username or display name -- used to derive the initial + hue. */
  name: string;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  /** Override initial with custom text. */
  override?: string;
}

const PALETTE = [
  "bg-brand text-white",
  "bg-ink text-white",
  "bg-brand-soft text-brand-ink",
  "bg-surface-sunken text-ink",
];

const SIZES: Record<NonNullable<Props["size"]>, string> = {
  xs: "h-7 w-7 text-xs",
  sm: "h-9 w-9 text-sm",
  md: "h-11 w-11 text-base",
  lg: "h-14 w-14 text-xl",
};

function hash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

export function Avatar({ name, size = "md", className, override }: Props) {
  const initial = (override ?? name ?? "?").trim().slice(0, 2).toUpperCase() || "?";
  const palette = PALETTE[hash(name ?? "") % PALETTE.length];
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex items-center justify-center rounded-full font-black font-display ring-2 ring-white shadow-card flex-shrink-0",
        SIZES[size],
        palette,
        className,
      )}
    >
      {initial}
    </span>
  );
}
